import { chain } from 'lodash';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toastError } from 'src/actions/toast';

import agentsResource from 'src/actions/agent/agentsResource';
import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import endpointsResource from 'src/actions/endpoints/endpointsResource';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import useResource, { getConsolidatedState, someFailed } from 'src/presentation/hooks/useResource';
import { DEFAULT_SLA_SECONDS } from 'src/models/RoutingGroup/RoutingGroup';
import { convertSecondsToMinutesAndSecondsString } from 'src/presentation/components/pages/shared/utils/TimeUtils';

const useInboxes = () => {
  const dispatch = useDispatch();
  const { values: agents, loadingState: agentsLoadingState } = useResource(agentsResource);
  const {
    values: inboxes,
    errors: inboxesErrors,
    loadingState: inboxesLoadingState,
  } = useResource(routingGroupsResource);
  const { values: communicationQueues, loadingState: loadingStateCommunication } =
    useResource(communicationQueuesResource);
  const { values: endpoints, loadingState: endpointsLoadingState } = useResource(endpointsResource);
  const { resetErrors } = routingGroupsResource.actions;
  const loadingState = getConsolidatedState([
    agentsLoadingState,
    endpointsLoadingState,
    inboxesLoadingState,
    loadingStateCommunication,
  ]);

  const failedState = someFailed(
    agentsLoadingState,
    endpointsLoadingState,
    inboxesLoadingState,
    loadingStateCommunication
  );

  useEffect(() => {
    if (failedState) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
      return;
    }
  }, [failedState, dispatch]);

  const values = useMemo(() => {
    const commQueueGroupedByInboxIdAndEndpointType = getCommQueueGroupedByInboxIdAndEndpointType({
      communicationQueues,
      endpoints,
    });

    return sortBy(
      map(inboxes, inbox => {
        const inboxAgents = get(inbox, 'agentIds', []);
        const agentNames = inboxAgents.map(inboxAgentId => {
          const agent = find(agents, agent => agent.id === inboxAgentId);
          return get(agent, 'name') || get(agent, 'email') || '';
        });
        const slaSeconds = get(inbox, 'slaSeconds', DEFAULT_SLA_SECONDS);
        const slaString = convertSecondsToMinutesAndSecondsString(slaSeconds);
        return { ...inbox, agentNames, slaString, entryPointsInfo: commQueueGroupedByInboxIdAndEndpointType[inbox.id] };
      }),
      item => item.name.toLowerCase()
    );
  }, [inboxes, agents, communicationQueues, endpoints]);

  return { errors: inboxesErrors, loadingState, resetErrors, values };
};

function getCommQueueGroupedByInboxIdAndEndpointType({ communicationQueues, endpoints }) {
  return chain(communicationQueues)
    .map(commQueue => ({
      ...commQueue,
      type: find(endpoints, endpoint => endpoint.id === commQueue.endpointId)?.type,
    }))
    .groupBy('routingGroupId')
    .reduce((gropedByRoutingGroupId, item, key) => {
      gropedByRoutingGroupId[key] = chain(item)
        .groupBy('type')
        .reduce((groupByType, commQueuesWithType, endpointType) => {
          groupByType[endpointType] = commQueuesWithType.length;
          return groupByType;
        }, {})
        .value();

      return gropedByRoutingGroupId;
    }, {})
    .value();
}

export default useInboxes;
